import { z } from "zod";
import { GRAPHICS_PRESET_IDS } from "./scene/graphics.ts";

/** Player preferences, persisted in localStorage only (SPEC §16). */

export const ONBOARDING_STEPS = ["move", "board", "wheel", "quest", "dig", "sell"] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

const SettingsSchema = z.object({
  name: z.string().max(32).default(""),
  sensitivity: z.number().min(0.1).max(3).default(1),
  invertY: z.boolean().default(false),
  master: z.number().min(0).max(1).default(0.8),
  effects: z.number().min(0).max(1).default(0.9),
  ambience: z.number().min(0).max(1).default(0.6),
  muted: z.boolean().default(false),
  onboarding: z.array(z.enum(ONBOARDING_STEPS)).default([]),
  onboardingHidden: z.boolean().default(false),
  graphicsPreset: z.enum(GRAPHICS_PRESET_IDS).default("balanced").catch("balanced"),
  contactShadowSamples: z.union([z.literal(24), z.literal(48)]).default(48).catch(48),
});

export type Settings = z.infer<typeof SettingsSchema>;

const KEY = "tiny-seas.settings.v1";

function load(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = SettingsSchema.safeParse(raw ? JSON.parse(raw) : {});
    if (parsed.success) return parsed.data;
  } catch {
    // Corrupt or unavailable storage: fall back to defaults.
  }
  return SettingsSchema.parse({});
}

type Listener = (s: Settings) => void;

class SettingsStore {
  readonly value: Settings = load();
  private readonly listeners = new Set<Listener>();

  update(patch: Partial<Settings>): void {
    Object.assign(this.value, patch);
    try {
      localStorage.setItem(KEY, JSON.stringify(this.value));
    } catch {
      // Storage full or blocked: settings still apply for this session.
    }
    for (const l of this.listeners) l(this.value);
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  completeStep(step: OnboardingStep): boolean {
    if (this.value.onboarding.includes(step)) return false;
    this.update({ onboarding: [...this.value.onboarding, step] });
    return true;
  }
}

export const settings = new SettingsStore();
